import { RequireScope } from "@/components/require-scope";
import { TableRowContextMenu } from "@/components/table-row-context-menu";
import { DotRow } from "@/components/ui/DotRow";
import { DotTable } from "@/components/ui/DotTable";
import type { Action } from "@/components/ui/MoreActions";
import { Text } from "@/components/ui/Text";
import { useSlugs } from "@/contexts/Sdk";
import { useRBAC } from "@/hooks/useRBAC";
import { cn } from "@/lib/utils";
import { formatRemoteMcpDisplay, mcpServerRouteParam } from "@/lib/sources";
import type { OrganizationMcpServer } from "@gram/client/models/components/organizationmcpserver.js";
import {
  invalidateAllOrganizationRemoteSessionClientMcpServers,
  useOrganizationRemoteSessionClientMcpServers,
} from "@gram/client/react-query/organizationRemoteSessionClientMcpServers.js";
import { useRemoveOrganizationRemoteSessionClientFromMcpServerMutation } from "@gram/client/react-query/removeOrganizationRemoteSessionClientFromMcpServer.js";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { Icon } from "@/components/ui/Icon";
import { useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import { toast } from "sonner";

// mcpServerHref builds a cross-project link to a remote-MCP server's detail page
// from the org-admin context. The org context has no project slug in its own
// URL, so we build the project-scoped path from the server's owning project slug
// (returned by the API) rather than a route helper, which can only resolve the
// current project. Returns null when the slug is unavailable.
function mcpServerHref(
  orgSlug: string | undefined,
  server: OrganizationMcpServer,
): string | null {
  if (!orgSlug || !server.projectSlug) return null;
  return `/${orgSlug}/projects/${server.projectSlug}/mcp/${mcpServerRouteParam(server)}/overview`;
}

export function McpServersTab({ clientId }: { clientId: string }): JSX.Element {
  const { orgSlug } = useSlugs();
  const queryClient = useQueryClient();
  const { hasAnyScope } = useRBAC();
  const canManage = hasAnyScope(["org:admin"]);
  const { data, isLoading, isError } =
    useOrganizationRemoteSessionClientMcpServers({
      clientId,
    });
  const remove = useRemoveOrganizationRemoteSessionClientFromMcpServerMutation({
    onSuccess: async () => {
      await invalidateAllOrganizationRemoteSessionClientMcpServers(
        queryClient,
        { refetchType: "all" },
      );
      toast.success("Removed client from MCP server");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to remove client from MCP server",
      );
    },
  });

  const items = data?.items ?? [];

  if (isError) {
    return (
      <Text className="text-destructive py-8 text-center">
        Failed to load attached MCP servers.
      </Text>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <Text muted className="py-8 text-center">
        This client is not attached to any MCP servers.
      </Text>
    );
  }

  return (
    <DotTable headers={[{ label: "MCP Server" }, { label: "" }]}>
      {items.map((server) => {
        const href = mcpServerHref(orgSlug, server);
        const label = formatRemoteMcpDisplay({
          name: server.name,
          url: server.url ?? "",
        });
        const actions: Action[] = [
          {
            label: "Remove from server",
            destructive: true,
            disabled: remove.isPending,
            onClick: () =>
              remove.mutate({
                request: {
                  removeClientFromMcpServerRequestBody: {
                    clientId,
                    mcpServerId: server.id,
                  },
                },
              }),
          },
        ];
        return (
          <TableRowContextMenu
            key={server.id}
            actions={canManage ? actions : []}
          >
            <DotRow
              icon={
                <Icon
                  name="network"
                  className="text-muted-foreground h-5 w-5"
                />
              }
              href={href ?? undefined}
              ariaLabel={href ? `View MCP server ${label}` : undefined}
            >
              <td className="px-3 py-3">
                <Text
                  variant="subheading"
                  as="div"
                  className={cn(
                    "truncate text-sm",
                    href &&
                      "group-hover:text-primary transition-colors group-hover:underline",
                  )}
                >
                  {label}
                </Text>
              </td>
              <td className="px-3 py-3 text-right">
                <RequireScope scope="org:admin" level="section">
                  <div
                    className="relative z-20"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="tertiary" size="sm">
                          <Button.LeftIcon>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button.LeftIcon>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {actions.map((action, index) => (
                          <DropdownMenuItem
                            key={index}
                            disabled={action.disabled}
                            onClick={() => action.onClick()}
                          >
                            {action.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </RequireScope>
              </td>
            </DotRow>
          </TableRowContextMenu>
        );
      })}
    </DotTable>
  );
}
